'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import layoutStyles from '@/styles/Layout.module.css';
import Dialog from '../common/Dialog';
import ZoomMeetingDetails from '../common/ZoomMeetingDetails';
import { SessionData } from './ProgramSession';

interface UpcomingEventsSectionProps {
  maxEvents?: number;
  showTitle?: boolean;
  variant?: 'default' | 'compact' | 'hero';
}

export const UpcomingEventsSection: React.FC<UpcomingEventsSectionProps> = ({ 
  maxEvents = 3, 
  showTitle = true,
  variant = 'default'
}) => {
  const [upcomingSessions, setUpcomingSessions] = useState<SessionData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedSession, setSelectedSession] = useState<SessionData | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  useEffect(() => {
    fetchUpcomingSessions();
  }, []);

  const fetchUpcomingSessions = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/meetings?type=next');
      if (!response.ok) throw new Error('Failed to fetch upcoming sessions');
      const data = await response.json();
      setUpcomingSessions(data.meetings.slice(0, maxEvents));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (session: SessionData) => {
    setSelectedSession(session);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedSession(null);
  };

  // Hero variant - looks like PowerOfPrioritization component
  if (variant === 'hero') {
    if (loading) {
      return (
        <section style={{
          position: 'relative',
          minHeight: '100vh',
          width: '100%',
          color: 'white',
          padding: '6rem 1rem',
          margin: 0,
          borderRadius: 0,
          overflow: 'hidden',
          textAlign: 'center',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          backgroundImage: 'url(/images/airfocus-f2C59x5uvn8-unsplash.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'scroll'
        }}>
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.73)',
            zIndex: 2,
          }} />
          <div className={layoutStyles.container} style={{ 
            position: 'relative',
            zIndex: 3,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '1.2rem', color: 'white' }}>
              Loading session information...
            </div>
          </div>
        </section>
      );
    }

    if (error || upcomingSessions.length === 0) {
      return null; // Don't show if no sessions
    }

    const session = upcomingSessions[0];

    return (
      <>
        <section 
          style={{
            position: 'relative',
            minHeight: '100vh',
            width: '100%',
            color: 'white',
            padding: '6rem 1rem',
            margin: 0,
            borderRadius: 0,
            overflow: 'hidden',
            textAlign: 'center',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            backgroundImage: 'url(/images/airfocus-f2C59x5uvn8-unsplash.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundAttachment: 'scroll'
          }}
        >
          {/* Background Image with Overlay */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1,
          }}>
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.73)',
              zIndex: 2,
            }} />
          </div>
          
          {/* Content Container */}
          <div className={layoutStyles.container} style={{ 
            position: 'relative',
            zIndex: 3,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center'
          }}>
            <h2 style={{ 
              fontSize: '2.5rem', 
              marginBottom: '1.5rem',
              fontWeight: '700',
              lineHeight: '1.2',
              textShadow: '0 2px 4px rgba(0,0,0,0.3)'
            }}>
              {session.title}
            </h2>
            {session.subtitle && (
              <h3 style={{
                fontSize: '1.5rem',
                marginBottom: '1.5rem',
                fontWeight: '500',
                lineHeight: '1.4',
                maxWidth: '800px',
                textShadow: '0 2px 4px rgba(0,0,0,0.3)'
              }}>
                {session.subtitle}
              </h3>
            )}
            
            <div style={{ 
              maxWidth: '700px',
              margin: '0 auto 2.5rem',
              fontSize: '1.1rem',
              lineHeight: '1.7',
              textShadow: '0 1px 2px rgba(0,0,0,0.5)',
              textAlign: 'center'
            }}>
              <p style={{ marginBottom: '1.2rem' }}>
                {session.summary}
              </p>
              <p>
                This program is specifically designed for those seeking guidance and support in their 
                educational and professional journey. You'll leave with practical tools and a clear 
                action plan to achieve your aspirations.
              </p>
            </div>

            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '1rem',
              marginTop: '2rem'
            }}>
              <button
                onClick={() => handleOpenDialog(session)}
                style={{
                  backgroundColor: '#3182ce',
                  color: 'white',
                  border: 'none',
                  padding: '0.75rem 2rem',
                  borderRadius: '50px',
                  fontSize: '1.1rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = '#2c5282';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = '#3182ce';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
                aria-label={`View ${session.title} meeting details and join information`}
                title={`Click to view meeting details and how to join the ${session.title} session`}
              >
                View Meeting Details
              </button>
            </div>
          </div>
        </section>

        {/* Meeting Details Dialog */}
        <Dialog
          isOpen={isDialogOpen}
          onClose={handleCloseDialog}
          title={`${session.title} - Meeting Details`}
          size="large"
        >
          <ZoomMeetingDetails sessionData={session} />
        </Dialog>
      </>
    );
  }

  // Default/compact variants for other pages
  if (loading) {
    return (
      <section style={{ padding: variant === 'compact' ? '2rem 0' : '4rem 0' }}>
        <div className={layoutStyles.container}>
          <div style={{ textAlign: 'center', fontSize: '1.1rem', color: 'var(--text-muted)' }}>
            Loading upcoming events...
          </div>
        </div>
      </section>
    );
  }

  if (error || upcomingSessions.length === 0) {
    return null; // Don't show section if there are no upcoming events
  }

  return (
    <>
      <section style={{ 
        padding: variant === 'compact' ? '2rem 1rem' : '4rem 1rem',
        backgroundColor: 'var(--background-color)',
        color: 'var(--text-color)'
      }}>
        <div style={{
          width: '100%',
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '0',
          boxSizing: 'border-box'
        }}>
          {showTitle && (
            <div style={{ 
              textAlign: 'center', 
              marginBottom: variant === 'compact' ? '2rem' : '3rem' 
            }}>
              <h2 style={{ 
                fontSize: variant === 'compact' ? '1.75rem' : '2.25rem',
                marginBottom: '1rem',
                color: 'var(--heading-color)'
              }}>
                Upcoming Events
              </h2>
              <p style={{ 
                color: 'var(--text-muted)',
                fontSize: '1.1rem',
                maxWidth: '600px',
                margin: '0 auto'
              }}>
                Join our upcoming sessions for Academic and Career Guidance. 
                All sessions are held on the 3rd Sunday at 1:00 PM PST / 4:00 PM EST.
              </p>
            </div>
          )}

          <div style={{ 
            display: 'grid',
            gap: variant === 'compact' ? '1.5rem' : '2rem',
            width: '100%'
          }}>
            {upcomingSessions.map((session) => (
              <div key={session.id} style={{
                backgroundColor: 'var(--card-bg)',
                borderRadius: '12px',
                padding: variant === 'compact' ? '1.5rem' : '2rem',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
                border: '1px solid var(--border-color)',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 8px 30px rgba(0, 0, 0, 0.1)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.05)';
              }}>
                <div style={{ 
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: '2rem',
                  flexWrap: 'wrap'
                }}>
                  <div style={{ flex: 1, minWidth: '300px' }}>
                    <h3 style={{ 
                      fontSize: variant === 'compact' ? '1.25rem' : '1.5rem',
                      marginBottom: '0.5rem',
                      color: 'var(--heading-color)'
                    }}>
                      {session.title}
                    </h3>
                    {session.subtitle && (
                      <h4 style={{ 
                        fontSize: variant === 'compact' ? '1rem' : '1.1rem',
                        marginBottom: '1rem',
                        color: 'var(--text-muted)',
                        fontWeight: '500'
                      }}>
                        {session.subtitle}
                      </h4>
                    )}
                    <p style={{ 
                      marginBottom: '1rem',
                      color: 'var(--text-muted)',
                      lineHeight: '1.6',
                      fontSize: variant === 'compact' ? '0.95rem' : '1rem'
                    }}>
                      {session.summary}
                    </p>
                    <div style={{ 
                      fontSize: variant === 'compact' ? '0.9rem' : '1rem',
                      color: 'var(--text-muted)',
                      fontWeight: '500',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}>
                      <svg 
                        xmlns="http://www.w3.org/2000/svg" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        stroke="currentColor" 
                        strokeWidth="2" 
                        strokeLinecap="round" 
                        strokeLinejoin="round"
                        style={{ width: '16px', height: '16px' }}
                      >
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                        <line x1="16" y1="2" x2="16" y2="6"></line>
                        <line x1="8" y1="2" x2="8" y2="6"></line>
                        <line x1="3" y1="10" x2="21" y2="10"></line>
                      </svg>
                      {new Date(session.meeting_date).toLocaleDateString('en-US', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                        timeZoneName: 'short'
                      })}
                    </div>
                  </div>
                  <div style={{ 
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.5rem',
                    alignItems: 'flex-end'
                  }}>
                    <button
                      onClick={() => handleOpenDialog(session)}
                      style={{
                        backgroundColor: 'var(--primary-color)',
                        color: 'white',
                        border: 'none',
                        padding: variant === 'compact' ? '0.5rem 1rem' : '0.75rem 1.5rem',
                        borderRadius: '8px',
                        fontSize: variant === 'compact' ? '0.9rem' : '1rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        whiteSpace: 'nowrap'
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                      }}
                      aria-label={`View ${session.title} meeting details`}
                      title={`Click to view meeting details for ${session.title}`}
                    >
                      View Details
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {showTitle && (
            <div style={{ 
              textAlign: 'center',
              marginTop: '2rem'
            }}>
              <Link
                href="/programs/academic-and-career-guidance"
                className="btn btn--primary"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '14px 32px',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  fontWeight: '600',
                  fontSize: '1.05rem'
                }}
                title="View all Academic and Career Guidance sessions"
                aria-label="Browse all upcoming and past Academic and Career Guidance sessions"
              >
                View All Sessions
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  viewBox="0 0 24 24" 
                  fill="currentColor" 
                  width="18" 
                  height="18"
                  style={{
                    transition: 'transform 0.2s ease'
                  }}
                >
                  <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z"/>
                </svg>
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Meeting Details Dialog */}
      <Dialog
        isOpen={isDialogOpen}
        onClose={handleCloseDialog}
        title={selectedSession ? `${selectedSession.title} - Meeting Details` : 'Meeting Details'}
        size="large"
      >
        {selectedSession && (
          <ZoomMeetingDetails sessionData={selectedSession} />
        )}
      </Dialog>
    </>
  );
};

export default UpcomingEventsSection;
